import { Vector2 } from "../../common/vector2.js";
import { clearNavBarLineProvider, setNavBarLineProvider } from "./navigation_bar_line.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { Game } from "../../game.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { ImgID } from "../../image_loader/img_id.js";
// import { LevelAssetsController } from "../../level_assets_controller.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { NetworkManager } from "../../network_manager.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { TelegramAPISettings } from "../../telegram_api_settings.js";
import { BasicEventsController } from "../../touch_events_controller/basic_events_controller.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../transforms/transform_straight_move.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIGradientOverlay } from "../common_ui_elements/ui_gradient_overlay.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { OverlayFactory } from "../overlay/overlay_factory.js";
import { OverlayId } from "../overlay/overlay_ids.js";
import { OverlayManager } from "../overlay/overlay_manager.js";
import { SafeAreas } from "../safe_areas.js";
import { NavigationItem } from "./navigation_item.js";
// import { VfxManager } from "../../webgl/vfx_manager.js";
import { randomRange } from "../../common/common_funcs.js";
// import { IController, StopBehavior } from "../../webgl/core/types/controller_types.js";

export class NavigationBlock extends UIElement
{
  private static readonly TELEGRAM_LINE_DOWN_DESIGN = 30;
  private static readonly MAIN_BG_ALIGN_UP_DESIGN = 0;
  private static readonly NAV_BG_LINE_SOURCE_Y = 128.5;
  private static readonly NAV_BG_TOP_CAP_SOURCE_H = 200;
  private static readonly NAV_BG_SOURCE_H = 400;
  private static readonly MAIN_BG_CONNECTOR_SOURCE_Y = 82.5;
  private static readonly MAIN_BG_TOP_CAP_SOURCE_H = 135;
  private static readonly MAIN_BG_SOURCE_H = 526;

  private events_controller!: BasicEventsController;

  private current_index = 1;
  /** Tab whose screen switch is in flight: a second tap on it does not restart the switch. */
  private pending_index: number | null = null;
  private pending_request = 0;

  private items: NavigationItem[] = [];
  private main_bg!: UINineSliceElement;
  /** Block-local y of the bar's top line; above it the canvas is transparent except the raised tab. */
  private bar_line_y = 0;
  private nav_bg: UINineSliceElement | null = null;
  private readonly bar_line_provider = (): number | null => this.barLineClientY();
  /** The current gesture started on a transparent part and goes to the screen underneath. */
  private passing_through = false;

  private readonly ITEM_IDS = [ImgID.IconNavigationShop, ImgID.IconNavigationMenu, ImgID.IconNavigationLeaderboard];
  private readonly ITEM_TEXTS = ["SHOP", "GAME", "LEADERBOARD.NAV"];
  // All three are persistent screen tabs. (Settings moved to a gear button on
  // the menu screen; leaderboard is now the third nav tab.)
  private readonly ITEM_SCREENS: Array<Screens | null> = [Screens.SHOP, Screens.MENU, Screens.LEADERBOARD];
  private readonly MAIN_GAP_Y = 96;

  // private static readonly MIN_VFX_GLOWS_COUNT = 1;
  // private static readonly MAX_VFX_GLOWS_COUNT = 3;
  // private static readonly VFX_GLOWS_INTERVAL = 3200;
  // private last_vfx_time = -99999;
  // private active_vfx_controllers: { controller: IController, expires_at: number }[] = [];

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
  )
  {
    super( ctx, x, y, width, height, false, false );

    this.initializeEventsController( this.ctx.canvas );
    setNavBarLineProvider( this.bar_line_provider );

    this.createBackground();
    this.createContent();
  }

  private createBackground(): void
  {
    console.log('NavigationBlock.createBackground');

    const x = 0;
    const content_h = this.rect.getH() - Game.safeAreaBottom();
    const is_telegram = TelegramAPISettings.isTelegramEnvironment();
    const y = is_telegram
      ? LayoutScaler.yFromDesign( NavigationBlock.TELEGRAM_LINE_DOWN_DESIGN )
      : 0;
    const w = this.rect.getW();
    const h = is_telegram
      ? Math.max( 1, content_h - y )
      : this.rect.getH();

    if ( is_telegram && Game.safeAreaBottom() > 0 )
    {
      const safe_area_bg = new RoundedRectangle(
        this.ctx,
        0,
        content_h,
        w,
        Game.safeAreaBottom(),
        0,
        "#0139CD"
      );
      safe_area_bg.setClickable( false );
      this.addChild( safe_area_bg );
    }

    if ( is_telegram && Game.safeAreaLeft() > 0 )
    {
      const left_safe_area = new RoundedRectangle(
        this.ctx, -Game.safeAreaLeft(), 0,
        Game.safeAreaLeft(), this.rect.getH(),
        0, "#0139CD"
      );
      left_safe_area.setClickable( false );
      this.addChild( left_safe_area );
    }

    if ( is_telegram && Game.safeAreaRight() > 0 )
    {
      const right_safe_area = new RoundedRectangle(
        this.ctx, w, 0,
        Game.safeAreaRight(), this.rect.getH(),
        0, "#0139CD"
      );
      right_safe_area.setClickable( false );
      this.addChild( right_safe_area );
    }

    const bg = new UINineSliceElement(
      this.ctx,
      x,
      y,
      w,
      h,
      ImgID.UI_NavigationBg,
      "navigation_bg"
    );
    
    this.addChild( bg );
    this.nav_bg = bg;
  }

  private async openCollection()
  {
    // const completed_levels = StatisticsStorage.load().completed_levels;
    // const stage = 1;

    // const images = completed_levels.map( level => LevelAssetsController.getImageId( level, stage ) );
    // const has_images = images.every( image => ImagesHolder.hasImage( image ) );

    // if ( has_images )
    // {
      // ScreenManager.showScreen( Screens.COLLECTION );

    //   return;
    // }

    // OverlayFactory.openOverlay( OverlayId.LOADING_OVERLAY );

    // for ( const level of completed_levels )
    // {
    //   await LevelAssetsController.loadLevelImage( level, stage );
    // }

    // OverlayManager.closeOverlay();

    // ScreenManager.show( Screens.COLLECTION );
  }

  private async openCalendar()
  {
    // const images = LevelAssetsController.getCalendarImageIds();
    // const has_images = images.every( image => ImagesHolder.hasImage( image ) );

    // if ( has_images )
    // {
    //   ScreenManager.show( Screens.CALENDAR );
    //   this.items.every( item => item.hideMsgIcon() );

    //   return;
    // }

    // OverlayFactory.openOverlay( OverlayId.LOADING_OVERLAY );

    // await LevelAssetsController.loadCalendarImages();

    // OverlayManager.closeOverlay();

    // ScreenManager.show( Screens.CALENDAR );
    // this.items.every( item => item.hideMsgIcon() );
  }

  private createContent(): void
  {
    const w = LayoutScaler.widthFromDesign( 340 );
    const h = LayoutScaler.heightFromDesign( 320 );
    const content_bottom = this.rect.getH() - Game.safeAreaBottom();

    const count = this.ITEM_IDS.length;

    const spacing =
      ( this.rect.getW() - count * w )
      / ( count + 1 );

    for ( let i = 0; i < count; i++ )
    {
      const x = spacing + i * ( w + spacing );
      const y = content_bottom - h;

      const item = new NavigationItem(
        this.ctx,
        x,
        y,
        w,
        h,
        this.ITEM_IDS[i],
        this.ITEM_TEXTS[i],
        i === 0
      )

      item.setOnClick( async () =>
      {
        console.log('NavigationBlock.onClick', i );

        const screen = this.ITEM_SCREENS[i];

        if ( screen === null || this.pending_index === i )
          return;

        if ( this.current_index === i )
        {
          if ( this.pending_index !== null )
          {
            ScreenManager.cancelPendingTabSwitch();
            this.pending_request++;
            this.pending_index = null;
          }
          return;
        }

        void audioController.playSound( AudioClips.UI_CLICK );

        if ( screen === Screens.LEADERBOARD && !NetworkManager.isOnline() )
        {
          NetworkManager.showOfflineLeaderboard();
          return;
        }

        // Ready tabs switch together with their highlight. A cold tab keeps the current
        // highlight until its images and UI are ready.
        this.pending_index = i;
        const request = ++this.pending_request;
        void ScreenManager.showScreen( screen ).finally( () =>
        {
          if ( this.pending_request === request )
            this.pending_index = null;
        } );
      } );

      this.items.push( item );
    }

    const bg_w = LayoutScaler.widthFromDesign( 439 );
    const bg_content_h = LayoutScaler.heightFromDesign( 372 );
    const is_telegram = TelegramAPISettings.isTelegramEnvironment();

    const image = this.items[this.current_index];

    const bg_x = image.pixelPosForRender().x + image.getRect().getW2() - bg_w / 2;
    let bg_h: number;
    let bg_y: number;
    const nav_bg_y = is_telegram
      ? LayoutScaler.yFromDesign( NavigationBlock.TELEGRAM_LINE_DOWN_DESIGN )
      : 0;
    const nav_bg_h = is_telegram
      ? Math.max( 1, content_bottom - nav_bg_y )
      : this.rect.getH();
    const renderedTopCapHeight = ( source_cap_h: number, source_image_h: number, rendered_h: number ): number =>
    {
      const initial_cap_h = LayoutScaler.cornerRadiusFromDesign( source_cap_h );
      const rendered_source_h = LayoutScaler.heightFromDesign( source_image_h );
      const fit_scale = rendered_source_h > rendered_h && rendered_source_h > 0
        ? rendered_h / rendered_source_h
        : 1;
      return Math.round( initial_cap_h * fit_scale );
    };
    const lineInTopCap = ( element_y: number, source_line_y: number, source_cap_h: number, rendered_cap_h: number ): number =>
      Math.floor( element_y ) + source_line_y / source_cap_h * rendered_cap_h;

    const nav_bg_top_cap_h = renderedTopCapHeight(
      NavigationBlock.NAV_BG_TOP_CAP_SOURCE_H,
      NavigationBlock.NAV_BG_SOURCE_H,
      nav_bg_h
    );
    const nav_bg_line_y = lineInTopCap(
      nav_bg_y,
      NavigationBlock.NAV_BG_LINE_SOURCE_Y,
      NavigationBlock.NAV_BG_TOP_CAP_SOURCE_H,
      nav_bg_top_cap_h
    );
    let mobile_alignment_correction_px = 0;
    let mobile_visual_offset_y = 0;
    let main_bg_top_cap_h = 0;
    let main_bg_connector_offset_y = 0;

    const updateMainBgCapGeometry = (): void =>
    {
      main_bg_top_cap_h = renderedTopCapHeight(
        NavigationBlock.MAIN_BG_TOP_CAP_SOURCE_H,
        NavigationBlock.MAIN_BG_SOURCE_H,
        bg_h
      );
      main_bg_connector_offset_y = NavigationBlock.MAIN_BG_CONNECTOR_SOURCE_Y
        / NavigationBlock.MAIN_BG_TOP_CAP_SOURCE_H
        * main_bg_top_cap_h;
    };

    if ( is_telegram )
    {
      const nav_line_y = nav_bg_line_y
        - LayoutScaler.yFromDesign( NavigationBlock.MAIN_BG_ALIGN_UP_DESIGN );
      bg_h = bg_content_h;
      updateMainBgCapGeometry();
      bg_y = nav_line_y - main_bg_connector_offset_y;

      const bottom_gap = this.rect.getH() - ( bg_y + bg_h );
      if ( bottom_gap > 0 )
      {
        bg_h += bottom_gap;
        updateMainBgCapGeometry();
        bg_y = nav_line_y - main_bg_connector_offset_y;
      }
    }
    else
    {
      bg_h = bg_content_h + Game.safeAreaBottom();
      bg_y = content_bottom - bg_content_h + LayoutScaler.yFromDesign( 40 );
      updateMainBgCapGeometry();

      if ( Game.isMobile() )
      {
        const main_bg_connector_y = Math.floor( bg_y ) + main_bg_connector_offset_y;
        mobile_alignment_correction_px = nav_bg_line_y - main_bg_connector_y;
        mobile_visual_offset_y = -2;
        bg_y += mobile_alignment_correction_px + mobile_visual_offset_y;
      }
    }

    const bg = new UINineSliceElement(
      this.ctx,
      bg_x,
      bg_y,
      bg_w,
      bg_h,
      ImgID.UI_NavigationMainBg,
      "selected_tab_nav"
    );

    this.main_bg = bg;
    this.bar_line_y = nav_bg_line_y;

    const main_bg_connector_y = Math.floor( bg_y ) + main_bg_connector_offset_y;
    console.log( "[NavigationBlock][line-alignment]", {
      renderer: "UINineSliceElement",
      navigation_rule: "navigation_bg",
      selected_rule: "selected_tab_nav",
      navigation_slice: { y1: 200, y2: 202, image_height: 400, mode: "both" },
      selected_slice: { y1: 135, y2: 500, image_height: 526, mode: "vertical" },
      is_mobile: Game.isMobile(),
      is_telegram,
      capacitor_platform: window.Capacitor?.getPlatform?.() ?? "web",
      nav_bg_line_y,
      main_bg_connector_y,
      delta_px: main_bg_connector_y - nav_bg_line_y,
      mobile_alignment_correction_px,
      mobile_visual_offset_y,
      nav_bg_top_cap_h,
      main_bg_top_cap_h,
      main_bg_connector_offset_y,
      nav_bg: { y: nav_bg_y, height: nav_bg_h },
      active_bg: { x: bg_x, y: bg_y, width: bg_w, height: bg_h },
      content_bottom,
      safe_area_bottom: Game.safeAreaBottom(),
      canvas_width: this.ctx.canvas.width,
      canvas_height: this.ctx.canvas.height,
      canvas_css_width: this.ctx.canvas.getBoundingClientRect().width,
      canvas_css_height: this.ctx.canvas.getBoundingClientRect().height,
      device_pixel_ratio: window.devicePixelRatio,
    } );

    this.addChild( bg );
    this.createNewMain( true );

    this.items.forEach( item =>
    {
      this.addChild( item );
    } );
  }

  public updateScreenBlock(): void
  {
    const current_screen = ScreenManager.getCurrentScreen();

    if ( !current_screen || !this.ITEM_SCREENS.includes( current_screen ) || this.ITEM_SCREENS.indexOf( current_screen ) === this.current_index )
      return;

    // this.clearActiveVfx();
    this.hideMain( this.current_index );
    this.current_index = this.ITEM_SCREENS.indexOf( current_screen );
    this.createNewMain();
  }

  private createNewMain( is_init = false ): void
  {
    const item = this.items[this.current_index];
    item.doMain( is_init );

    const new_bg_x = item.pixelPosForRender().x + item.getRect().getW2() - this.main_bg.getRect().getW2();
    this.main_bg.setPixelPos( new Vector2( new_bg_x, this.main_bg.pixelPosForRender().y ), true );
  }

  private hideMain( index: number ): void
  {
    const item = this.items[index];
    item.doDefault();
  }

  public name(): string
  {
    return "NavigationBlock";
  }

  public update( now: number ): void
  {
    super.updateBasic( now );

    if ( this.ctx.canvas.style.display === "none" )
      return;

    // this.active_vfx_controllers = this.active_vfx_controllers.filter( item => item.expires_at > now );

    // if ( now - this.last_vfx_time > NavigationBlock.VFX_GLOWS_INTERVAL )
    // {
    //   const pre_warm = this.last_vfx_time === -99999;
    //   this.last_vfx_time = now;
    //   this.spawnGlows( now, pre_warm );
    // }
  }

  // private spawnGlows( now: number, pre_warm: boolean = false ): void
  // {
  //   if ( !this.main_bg )
  //     return;

  //   const count = Math.round( randomRange( NavigationBlock.MIN_VFX_GLOWS_COUNT, NavigationBlock.MAX_VFX_GLOWS_COUNT ) );

  //   const bg_rect = this.main_bg.getRect();
  //   const bg_pos = this.main_bg.getAbsolutePosition();
    
  //   const canvas_y_offset = Game.screenH() - ( LayoutScaler.yFromDesign( 250 ) + Game.safeAreaBottom() ) - Game.safeAreaTop();

  //   const w_factor = bg_rect.getW() * 0.6;
  //   const h_factor = bg_rect.getH() * 0.6;
  //   const offset_x = bg_rect.getW() * 0.2;
  //   const offset_y = bg_rect.getH() * 0.2;

  //   for ( let i = 0; i < count; i++ )
  //   {
  //     const rx = bg_pos.x + offset_x + Math.random() * w_factor;
  //     const ry = bg_pos.y + offset_y + Math.random() * h_factor;
  //     const start_time = pre_warm ? now - Math.random() * 4000 : now + Math.random() * NavigationBlock.VFX_GLOWS_INTERVAL;

  //     const controller = VfxManager.spawnNavigationButtonGlow( new Vector2( rx, ry + canvas_y_offset ), start_time, { x: bg_pos.x, y: bg_pos.y + canvas_y_offset, w: bg_rect.getW(), h: bg_rect.getH() }, { intensity: 2, min_size: 40, max_size: 60 } );
  //     if ( controller )
  //       this.active_vfx_controllers.push( { controller, expires_at: start_time + 4000 } );
  //   }
  // }

  // public resetGlowTimer(): void
  // {
  //   this.last_vfx_time = 0;
  // }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.ctx.clearRect( -Game.safeAreaLeft(), -Game.safeAreaTop(), Game.screenW() + Game.safeAreaLeft() + Game.safeAreaRight(), Game.screenH() + Game.safeAreaTop() + Game.safeAreaBottom() );

    this.startRender();
    this.finishRender();

    // SafeAreas.renderBottomSafeAreaOverlayForNavigation( this.ctx, this.rect.getH() + LayoutScaler.heightFromDesign( this.MAIN_GAP_Y ) );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );

    // The canvas is taller than the bar (room for the raised selected tab), and the items' hit
    // rects reach above the line too: a tap on the screen's own controls right above the bar (the
    // leaderboard's Down/Up pill) landed here and switched tabs. Gestures that start on the
    // transparent part go to the screen underneath instead. Touch events stay on the canvas they
    // started on, so they are forwarded; with a mouse several screens (leaderboard, offers) listen on
    // the window and already get the click themselves, so there the bar only has to let it be.
    const canvas_el = this.ctx.canvas;
    const forward = ( phase: "start" | "move" | "end", p: Vector2 ): void =>
    {
      if ( Game.isMobile() )
        ScreenManager.forwardTouchToCurrentUi( phase, p, canvas_el );
    };
    this.events_controller.init(
      ( p: Vector2 ) =>
      {
        this.passing_through = this.isTransparentAt( p );
        if ( this.passing_through )
          forward( "start", p );
        else
          super.onTouchStart( p, performance.now() );
      },
      ( p: Vector2 ) =>
      {
        if ( this.passing_through )
          forward( "end", p );
        else
          super.onTouchEnd( p, performance.now() );
        this.passing_through = false;
      },
      ( p: Vector2 ) =>
      {
        if ( this.passing_through )
          forward( "move", p );
        else
          super.onTouchMove( p, performance.now() );
      }
    );
  }

  /**
   * Client y of the bar's line as drawn. In DOM mode it is read from the background's border-image:
   * the browser shrinks the caps when they do not fit the box, so `bar_line_y` (cap math in UI px)
   * can be a few px off the orange line on screen.
   */
  private barLineClientY(): number | null
  {
    const skin = this.nav_bg?.getDomNode().querySelector<HTMLElement>( ".ui-dom-skin" );
    if ( skin )
    {
      const box = skin.getBoundingClientRect();
      const cs = getComputedStyle( skin );
      const top = parseFloat( cs.borderImageWidth.split( " " )[0] ) || parseFloat( cs.borderTopWidth );
      const bottom = parseFloat( cs.borderImageWidth.split( " " )[2] ?? "" ) || parseFloat( cs.borderBottomWidth );
      if ( box.height > 0 && top > 0 )
      {
        const fit = Math.min( 1, box.height / ( top + ( bottom || 0 ) ) );
        return box.top + top * fit * NavigationBlock.NAV_BG_LINE_SOURCE_Y / NavigationBlock.NAV_BG_TOP_CAP_SOURCE_H;
      }
    }
    const el: Element = this.getDomHost() ?? this.ctx.canvas;
    const box = el.getBoundingClientRect();
    if ( box.height <= 0 || this.rect.getH() <= 0 ) return null;
    return box.top + ( this.pixelPosImportant().y + this.bar_line_y ) * box.height / this.rect.getH();
  }

  /** Above the bar's line and outside the raised selected tab: nothing of the bar is drawn there. */
  private isTransparentAt( p: Vector2 ): boolean
  {
    const origin = this.pixelPosForRender();
    const local_x = p.x - origin.x;
    const local_y = p.y - origin.y;
    if ( local_y >= this.bar_line_y - LayoutScaler.heightFromDesign( 4 ) )
      return false;
    if ( !this.main_bg )
      return true;
    const tab = this.main_bg.pixelPosForRender();
    const tab_rect = this.main_bg.getRect();
    const in_tab = local_x >= tab.x && local_x <= tab.x + tab_rect.getW()
      && local_y >= tab.y && local_y <= tab.y + tab_rect.getH();
    return !in_tab;
  }

  // public clearActiveVfx(): void
  // {
  //   this.active_vfx_controllers.forEach( item => item.controller.stop( StopBehavior.ClearInstances ) );
  //   this.active_vfx_controllers = [];
  // }

  public destroy(): void
  {
    console.log( 'NavigationBlock.destroy' );
    clearNavBarLineProvider( this.bar_line_provider );
    this.cleanup();

    // this.clearActiveVfx();

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null as any;
    }
  }
}
